"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import { TProfile } from "@/features/profile/utils/types";
import { useRouter } from "next/navigation";
import toast from "react-hot-toast";
import { useProfile } from "@/features/profile/hooks/useProfile";
import { FaCircleInfo } from "react-icons/fa6";

export interface AuthState {
  token: string;
  user: TProfile;
  temporaryToken?: string;
}

interface AuthContextType {
  authState: AuthState | null;
  setAuthState: React.Dispatch<React.SetStateAction<AuthState | null>>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

const getInitState = () => {
  let sessionString;
  let session;

  if (typeof window !== "undefined") {
    sessionString = sessionStorage.getItem("session");
    session = sessionString ? JSON.parse(sessionString) : null;
  }
  return session;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [authState, setAuthState] = useState<AuthState | null>(getInitState());
  const router = useRouter();

  useEffect(() => {
    if (!authState?.user) return;
  }, [authState?.user]);

  useEffect(() => {
    const sessionString = sessionStorage.getItem("session");
    const session = sessionString ? JSON.parse(sessionString) : null;
    if (authState?.user?.is_onboarded === false && session?.token !== "") {
      toast(() => (
        <span className="flex items-center">
          <FaCircleInfo className="mr-2 text-lg text-primary-base" />
          Please complete your profile.
        </span>
      ));
      router.push("/e-services/onboarding");
    }
  }, [authState?.user?.is_onboarded, router]);

  const logout = () => {
    router.replace("/e-services");
    setTimeout(() => {
      setAuthState(null);
      sessionStorage.clear();
    }, 300);
  };

  const value = { authState, setAuthState, logout };

  return (
    <AuthContext.Provider value={value}>
      <ProfileWatcher />
      {children}
    </AuthContext.Provider>
  );
};

function ProfileWatcher() {
  useProfile();
  return null;
}
